import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useMutation } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import Recorder from 'recorder-core';
import 'recorder-core/src/engine/wav.js';
import AudioRecorderControls from '../components/AudioRecorderControls';
import DiagnosisReport from '../components/DiagnosisReport';
import AlertOverlay from '../components/AlertOverlay';
import { supabase } from '../supabaseClient';
import '../styles/Practice.css';
import '../styles/Layout.css';
import '../styles/Introduction.css';
import '../styles/CustomPractice.css';

export default function CustomPractice() {
  const session = useSession();
  const language = session?.user?.user_metadata?.language || 'en_us';
  const [sentenceInput, setSentenceInput] = useState('');
  const [preparedSentence, setPreparedSentence] = useState('');
  const [prepareError, setPrepareError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [mediaError, setMediaError] = useState('');
  const [isAcquiringMedia, setIsAcquiringMedia] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);

  const recorderRef = useRef(null);
  const recordingStreamRef = useRef(null);

  const charCount = useMemo(() => sentenceInput.trim().length, [sentenceInput]);
  const hasPreparedSentence = !!preparedSentence;
  const hasDiagnosisLog = !!analysisResult;

  const { mutate: analyzeRecording, isPending: isAnalyzingRecording } = useMutation({
    mutationFn: async (audioBlob) => {
      if (!audioBlob || !preparedSentence) {
        throw new Error('Missing audio or prepared sentence.');
      }

      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.wav');
      formData.append('target_content', preparedSentence);
      formData.append('language', language);

      const { data, error } = await supabase.functions.invoke('analyze-speech', { body: formData });

      if (error) {
        if (error instanceof FunctionsHttpError) {
          const errorJson = await error.context.json();
          throw new Error(errorJson.error || error.message);
        }
        throw error;
      }

      return data;
    },
    onSuccess: (data) => {
      setAnalysisResult(data);
    },
    onError: (error) => {
      setMediaError(error instanceof Error ? error.message : 'Analysis failed.');
    },
  });

  const handlePrepareSentence = () => {
    const trimmedSentence = sentenceInput.trim();
    if (!trimmedSentence) {
      setPrepareError('Please enter a sentence first.');
      return;
    }

    setPrepareError('');
    setPreparedSentence(trimmedSentence);
    setAnalysisResult(null);
  };

  const handleResetSentence = () => {
    setPreparedSentence('');
    setPrepareError('');
    setSentenceInput('');
    setAnalysisResult(null);
    setMediaError('');
    setTimer(0);
  };

  const handleNextSentence = () => {
    setPreparedSentence('');
    setPrepareError('');
    setSentenceInput('');
    setAnalysisResult(null);
    setMediaError('');
    setTimer(0);
  };

  const handleTryAgain = () => {
    setAnalysisResult(null);
    setMediaError('');
    setTimer(0);
  };

  useEffect(() => {
    let intervalId;
    if (isRecording) {
      intervalId = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }
    return () => clearInterval(intervalId);
  }, [isRecording]);

  const stopRecordingStream = () => {
    if (recordingStreamRef.current) {
      recordingStreamRef.current.getTracks().forEach((track) => track.stop());
      recordingStreamRef.current = null;
    }
  };

  const handleRecordToggle = async () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        (blob) => {
          setIsRecording(false);
          setTimer(0);
          analyzeRecording(blob);
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          setMediaError(`Recording error: ${err}`);
          setIsRecording(false);
          stopRecordingStream();
        }
      );
      return;
    }

    setMediaError('');
    let selectedStream = null;

    if (selectedDeviceId) {
      try {
        selectedStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: selectedDeviceId } },
        });
        recordingStreamRef.current = selectedStream;
      } catch (err) {
        setMediaError(err instanceof Error ? err.message : 'Microphone access failed.');
        return;
      }
    }

    const rec = Recorder({
      type: 'wav',
      sampleRate: 16000,
      bitRate: 16,
      sourceStream: selectedStream || undefined,
    });

    setIsAcquiringMedia(true);
    rec.open(
      () => {
        setIsAcquiringMedia(false);
        rec.start();
        recorderRef.current = rec;
        setIsRecording(true);
        setTimer(0);
      },
      (err, isUserNotAllow) => {
        setIsAcquiringMedia(false);
        setMediaError(isUserNotAllow ? 'Microphone access was denied.' : `Could not open recorder: ${err}`);
        stopRecordingStream();
      }
    );
  };

  const handleDeleteRecording = () => {
    if (recorderRef.current && isRecording) {
      recorderRef.current.stop(
        () => {
          setIsRecording(false);
          setTimer(0);
          recorderRef.current.close();
          recorderRef.current = null;
          stopRecordingStream();
        },
        (err) => {
          setMediaError(`Recording error: ${err}`);
          setIsRecording(false);
          stopRecordingStream();
        }
      );
    }
  };

  const formatTime = (seconds) =>
    `${Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0')}:${(seconds % 60).toString().padStart(2, '0')}`;

  const isProcessing = isAnalyzingRecording || isAcquiringMedia;

  return (
    <>
      <main className="main-content width-practice practice-page-shell custom-practice-page">
        <h1 className="page-title">Custom Practice</h1>

        <div className="custom-practice-meta">
          <span className="custom-practice-chip custom-practice-chip-accent">Not Saved</span>
          <span className="custom-practice-chip">ASR Only</span>
          <span className="custom-practice-chip">Bring Your Own Sentence</span>
        </div>

        <section className="custom-practice-input-shell">
          <div className="custom-practice-input-head">
            <div>
              <p className="custom-practice-eyebrow">Target Sentence</p>
              <h2 className="custom-practice-heading">Type what you want to practice</h2>
            </div>
            <span className="custom-practice-count">0 / 220</span>
          </div>

          <label className="custom-practice-field">
            <div className="custom-practice-input-shell-row">
              <input
                type="text"
                className="custom-practice-input"
                placeholder="Enter a sentence here. Example: We should confirm the meeting agenda before noon."
                value={sentenceInput}
                onChange={(event) => setSentenceInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handlePrepareSentence();
                  }
                }}
              />
              <button
                type="button"
                className="custom-practice-submit"
                aria-label="Use sentence"
                onClick={handlePrepareSentence}
                disabled={!sentenceInput.trim()}
              >
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M5 12h12m-5-5 5 5-5 5"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </label>

          {prepareError && <p className="custom-practice-inline-error">{prepareError}</p>}

          {preparedSentence && (
            <div className="custom-practice-ready-banner">
              <div className="custom-practice-ready-copy">
                <span className="custom-practice-ready-label">Sentence Ready</span>
                <p className="custom-practice-ready-text">{preparedSentence}</p>
              </div>
              {!hasDiagnosisLog && (
                <button
                  type="button"
                  className="custom-practice-ready-state custom-practice-ready-action"
                  onClick={handleResetSentence}
                  disabled={isProcessing}
                >
                  Change
                </button>
              )}
            </div>
          )}

          <div className="custom-practice-notes">
            <div className="custom-practice-note">
              <strong>Instant session</strong>
              <span>Nothing is written to your course, progress, or records.</span>
            </div>
            <div className="custom-practice-note">
              <strong>Simple loop</strong>
              <span>Set a sentence first, then record and analyze without a report step.</span>
            </div>
          </div>
        </section>

        {hasPreparedSentence && (
          <div className="practice-area custom-practice-sentence-stage">
            <p className="practice-text">{preparedSentence}</p>
          </div>
        )}

        {mediaError && <div className="custom-practice-status-alert custom-practice-status-error">{mediaError}</div>}
        {isAcquiringMedia && (
          <div className="custom-practice-status-alert custom-practice-status-info">Accessing microphone...</div>
        )}

        {hasDiagnosisLog && (
          <DiagnosisReport
            result={analysisResult}
            nextAction={
              <>
                <button className="practice-btn" onClick={handleTryAgain} disabled={isProcessing}>
                  Try Again
                </button>
                <button className="practice-btn primary" onClick={handleNextSentence} disabled={isProcessing}>
                  Next
                </button>
              </>
            }
          />
        )}
      </main>

      <AudioRecorderControls
        isRecording={isRecording}
        recordDisabled={!hasPreparedSentence || isProcessing || hasDiagnosisLog}
        deleteDisabled={!isRecording}
        timerText={formatTime(timer)}
        onRecordToggle={handleRecordToggle}
        onDelete={handleDeleteRecording}
        selectedDeviceId={selectedDeviceId}
        onDeviceChange={setSelectedDeviceId}
        micDisabled={!hasPreparedSentence || isProcessing || hasDiagnosisLog || isRecording}
      />

      {isAnalyzingRecording && (
        <AlertOverlay message="Analyzing..." />
      )}
    </>
  );
}
